import { useEffect, useMemo, useState } from 'react';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { SvpCategory } from '@vgtv/api-client/lib/svp_category';

import { useAppDispatch, useAppSelector } from '../../modules/hooks';
import { selectIsRootCategoryMatched } from '../../modules/categories';
import { fetchSvpAssetCategoryMatch } from '../../modules/assets/actions';

const toArray = <T>(value: T | T[]): T[] => {
  return Array.isArray(value) ? value : [value];
};

const isMatched = (matches: (boolean | undefined)[]): boolean | undefined => {
  if (matches.some(Boolean)) {
    return true;
  }

  if (matches.some((match) => match === undefined)) {
    return undefined;
  }

  return false;
};

const useAssetCategoryMatch = (
  categoryIdToMatch: SvpCategory['id'] | SvpCategory['id'][],
  asset?: SvpAssetAdditional
) => {
  const dispatch = useAppDispatch();

  const categoryIds = useMemo(
    () => toArray(categoryIdToMatch),
    [categoryIdToMatch]
  );

  const isAssetCategoryMatched = useAppSelector((state) =>
    isMatched(
      categoryIds.map((categoryId) =>
        selectIsRootCategoryMatched(
          state,
          categoryId,
          asset?.provider,
          asset?.category.id
        )
      )
    )
  );

  const isAssetParentCategoryMatched = useAppSelector((state) =>
    isMatched(
      categoryIds.map((categoryId) =>
        selectIsRootCategoryMatched(
          state,
          categoryId,
          asset?.provider,
          asset?.category.parentId
        )
      )
    )
  );

  const isAssetCategoryMatchFetched = isAssetCategoryMatched !== undefined;
  const isAssetParentCategoryMatchFetched =
    isAssetParentCategoryMatched !== undefined;

  const [isFetching, setIsFetching] = useState(
    Boolean(
      asset &&
        !isAssetCategoryMatchFetched &&
        !isAssetParentCategoryMatchFetched
    )
  );

  useEffect(() => {
    if (
      !asset ||
      isAssetCategoryMatchFetched ||
      isAssetParentCategoryMatchFetched
    ) {
      setIsFetching(false);

      return;
    }

    setIsFetching(true);

    Promise.all(
      categoryIds.map((categoryId) =>
        dispatch(fetchSvpAssetCategoryMatch({ asset, categoryId }))
      )
    )
      .then(() => {
        setIsFetching(false);
      })
      .catch(() => {
        setIsFetching(false);
      });
  }, [
    dispatch,
    isAssetCategoryMatchFetched,
    isAssetParentCategoryMatchFetched,
    asset,
    categoryIds,
  ]);

  return {
    isMatched: Boolean(isAssetCategoryMatched || isAssetParentCategoryMatched),
    isFetching,
  };
};

export { useAssetCategoryMatch };
